import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Send, Pencil, Eye, EyeOff, Users, Share2, Link2Off, ThumbsUp, BadgeCheck, Copy, Import, Loader2 } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatPeriodLabel, formatDate } from "@/lib/format";
import { Switch } from "@/components/ui/switch";

function StreakCard({ companyId }) {
  const [streak, setStreak] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (companyId) api.get(`/companies/${companyId}/streak`).then(({ data }) => setStreak(data)).catch(() => {});
  }, [companyId]);
  if (!streak) return null;
  const badgeUrl = `${API}/badge/${streak.badge_token}.svg`;
  const verifyUrl = `${API}/v/${streak.badge_token}`;
  const htmlSnippet = `<a href="${verifyUrl}"><img src="${badgeUrl}" alt="Transparent Founder - verified on TenX" height="56"></a>`;
  const mdSnippet = `[![Transparent Founder](${badgeUrl})](${verifyUrl})`;
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Could not copy"); }
  };
  return (
    <Card className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4" data-testid="streak-card">
      <div className="flex items-center gap-3">
      <BadgeCheck className="h-8 w-8 text-brand-ink" strokeWidth={1.5} />
        <div>
          <div className="text-sm font-bold text-foreground" data-testid="streak-count">
            {streak.streak_months > 0 ? `${streak.streak_months}-month update streak` : "Start your update streak"}
          </div>
          <div className="text-xs text-muted-foreground">
            {streak.streak_months > 0 ? "Publish this month to keep it alive. Investors notice." : "Publish an update every month to earn a public Transparent Founder badge."}
          </div>
        </div>
        <div className="ml-2 hidden gap-1 sm:flex">
          {streak.months.slice(-6).map((m, i) => (
            <div key={m.month} title={m.month} data-testid={`streak-month-${i}`} className={`h-4 w-4 rounded-sm ${m.published ? "bg-success" : "bg-secondary"}`} />
          ))}
        </div>
      </div>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="streak-badge-btn">
        <BadgeCheck className="h-3.5 w-3.5" /> Get your badge
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="badge-dialog">
          <DialogHeader>
            <DialogTitle>Transparent Founder badge</DialogTitle>
            <DialogDescription>Embed it in your email signature, deck or README. It links to a public, timestamp-verified page.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center rounded-lg border bg-secondary/40 p-4">
            <img src={badgeUrl} alt="Transparent Founder badge" width={252} height={56} className="mx-auto" data-testid="badge-preview" />
          </div>
          <div className="space-y-2">
            {[["HTML (email signature)", htmlSnippet], ["Markdown (README)", mdSnippet], ["Verify link", verifyUrl]].map(([label, text]) => (
              <div key={label}>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="flex items-center gap-1.5">
                  <code className="min-w-0 flex-1 truncate rounded border bg-secondary/40 px-2 py-1.5 text-[10px]">{text}</code>
                  <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={() => copy(text, label)} data-testid={`copy-${label.split(" ")[0].toLowerCase()}`}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function UpdatesList() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [imp, setImp] = useState({ title: "", sent_date: "", body_markdown: "", tldr: "" });
  const [importing, setImporting] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [visUpdate, setVisUpdate] = useState(null);
  const [investors, setInvestors] = useState([]);
  const hasImported = updates.some((u) => u.status === "imported");

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try { const { data } = await api.get(`/companies/${company.id}/updates`); setUpdates(data); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const remove = async (id) => {
    try { await api.delete(`/updates/${id}`); toast.success("Update deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const share = async (u) => {
    if (!u.share_token && u.delivery_settings?.mode !== "verified_link" && !window.confirm("Create a public read-only link? Anyone with this link can read without email verification.")) return;
    try {
      const { data } = await api.post(`/updates/${u.id}/share-link`);
      const url = `${window.location.origin}/api/s/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(data.verification_required ? "Email-verified link copied to clipboard" : "Public read-only link copied to clipboard");
      } catch {
        toast.success(`Share link: ${url}`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create share link"); }
  };

  const revokeShare = async (u) => {
    try { await api.delete(`/updates/${u.id}/share-link`); toast.success("Shared link revoked"); load(); }
    catch { toast.error("Could not revoke link"); }
  };

  const submitImport = async () => {
    if (!imp.body_markdown.trim()) { toast.error("Paste the update content to import"); return; }
    setImporting(true);
    try {
      await api.post(`/companies/${company.id}/updates/import`, imp);
      toast.success("Past update added to your archive");
      setImportOpen(false);
      setImp({ title: "", sent_date: "", body_markdown: "", tldr: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not import update"); }
    finally { setImporting(false); }
  };

  const submitBulkImport = async () => {
    if (!bulkText.trim()) { toast.error("Paste your past updates to import"); return; }
    setImporting(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/updates/bulk-import`, { text: bulkText });
      if (!data.added) { toast.error("Couldn't find any updates - separate each with a line of ---"); return; }
      toast.success(`${data.added} past update${data.added === 1 ? "" : "s"} added to your archive`);
      setImportOpen(false); setBulkText(""); setBulkMode(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not import updates"); }
    finally { setImporting(false); }
  };

  const toggleMasterVisibility = async (u, visible) => {
    setUpdates((list) => list.map((x) => (x.id === u.id ? { ...x, investor_visible: visible } : x)));
    try { await api.patch(`/updates/${u.id}/investor-visibility`, { visible }); }
    catch { toast.error("Could not update visibility"); load(); }
  };

  const openVisDialog = async (u) => {
    setVisUpdate(u);
    try { const { data } = await api.get(`/companies/${company.id}/investors`); setInvestors(data); }
    catch { setInvestors([]); }
  };

  const togglePerInvestor = async (investorId, visible) => {
    if (!visUpdate) return;
    const hidden = visUpdate.investor_hidden_ids || [];
    const nextHidden = visible ? hidden.filter((id) => id !== investorId) : [...new Set([...hidden, investorId])];
    const next = { ...visUpdate, investor_hidden_ids: nextHidden };
    setVisUpdate(next);
    setUpdates((list) => list.map((x) => (x.id === next.id ? next : x)));
    try { await api.patch(`/updates/${visUpdate.id}/investor-visibility/${investorId}`, { visible }); }
    catch { toast.error("Could not update investor visibility"); load(); }
  };

  const bulkVisibility = async (visible) => {
    try {
      const { data } = await api.post(`/companies/${company.id}/updates/imported/investor-visibility`, { visible });
      toast.success(`${data.updated} imported update${data.updated === 1 ? "" : "s"} ${visible ? "shown to" : "hidden from"} investors`);
      load();
    } catch { toast.error("Could not update visibility"); }
  };

  const CTA = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-update-button">
        <Import className="h-4 w-4" /> Import past update
      </Button>
      <Button onClick={() => navigate("/app/updates/new")} className="gap-2" data-testid="new-update-button">
        <Plus className="h-4 w-4" /> New Update
      </Button>
    </div>
  );

  return (
    <AppShell title="Updates" subtitle="Drafts and published investor updates" actions={CTA}>
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import past update{bulkMode ? "s" : ""}</DialogTitle>
            <DialogDescription>Sent investor updates by email before TenX? Bring them here so your whole history lives in one place. Saved to your archive only - it won't email anyone.</DialogDescription>
          </DialogHeader>
          <div className="mb-1 inline-flex rounded-lg border bg-secondary/40 p-0.5 text-xs" data-testid="import-mode-toggle">
            <button type="button" onClick={() => setBulkMode(false)} className={`rounded-md px-3 py-1.5 font-medium transition-colors ${!bulkMode ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid="import-mode-single">One update</button>
            <button type="button" onClick={() => setBulkMode(true)} className={`rounded-md px-3 py-1.5 font-medium transition-colors ${bulkMode ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid="import-mode-bulk">Multiple updates</button>
          </div>
          {bulkMode ? (
            <div className="space-y-2">
              <Label className="text-xs">Paste your past updates</Label>
              <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={11} className="font-mono text-xs" data-testid="import-bulk-body"
                placeholder={"Title: March 2024 Update\nDate: 2024-03-31\nRevenue hit $40k MRR this month...\n\n---\n\nTitle: February 2024 Update\nDate: 2024-02-29\nClosed two enterprise pilots..."} />
              <p className="text-xs text-muted-foreground">Separate each update with a line containing only <code className="rounded bg-secondary px-1">---</code>. Optional <code className="rounded bg-secondary px-1">Title:</code> and <code className="rounded bg-secondary px-1">Date: YYYY-MM-DD</code> lines at the top of each.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={imp.title} onChange={(e) => setImp((s) => ({ ...s, title: e.target.value }))} placeholder="March 2024 update" className="mt-1" data-testid="import-title" />
                </div>
                <div>
                  <Label className="text-xs">Date originally sent</Label>
                  <Input type="date" value={imp.sent_date} onChange={(e) => setImp((s) => ({ ...s, sent_date: e.target.value }))} className="mt-1" data-testid="import-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs">TL;DR (optional)</Label>
                <Input value={imp.tldr} onChange={(e) => setImp((s) => ({ ...s, tldr: e.target.value }))} placeholder="One-line summary" className="mt-1" data-testid="import-tldr" />
              </div>
              <div>
                <Label className="text-xs">Update content</Label>
                <Textarea value={imp.body_markdown} onChange={(e) => setImp((s) => ({ ...s, body_markdown: e.target.value }))} placeholder="Paste the body of the email update. Markdown supported." rows={8} className="mt-1" data-testid="import-body" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="import-cancel">Cancel</Button>
            {bulkMode ? (
              <Button onClick={submitBulkImport} disabled={importing} className="gap-1.5" data-testid="import-bulk-submit">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Import className="h-4 w-4" />} Import all
              </Button>
            ) : (
              <Button onClick={submitImport} disabled={importing} className="gap-1.5" data-testid="import-submit">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Import className="h-4 w-4" />} Add to archive
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!visUpdate} onOpenChange={(o) => !o && setVisUpdate(null)}>
        <DialogContent className="sm:max-w-md" data-testid="per-investor-dialog">
          <DialogHeader>
            <DialogTitle>Who can see this update?</DialogTitle>
            <DialogDescription>"{visUpdate?.title}" - turn off any investor who shouldn't see this imported update in their portal.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {investors.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No investors yet.</p>
            ) : investors.map((inv) => {
              const shown = !(visUpdate?.investor_hidden_ids || []).includes(inv.id);
              return (
                <div key={inv.id} className="flex items-center justify-between rounded-lg border bg-card px-3 py-2" data-testid={`per-investor-row-${inv.id}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{inv.name || inv.email}</div>
                    <div className="truncate text-xs text-muted-foreground">{inv.email}</div>
                  </div>
                  <Switch checked={shown} onCheckedChange={(v) => togglePerInvestor(inv.id, v)} data-testid={`per-investor-toggle-${inv.id}`} />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisUpdate(null)} data-testid="per-investor-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {company && <StreakCard companyId={company.id} />}
      {hasImported && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-2.5" data-testid="imported-visibility-bar">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Import className="h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span>Imported updates appear in investors' portals by default. Use the toggles to control who sees them.</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => bulkVisibility(true)} data-testid="bulk-show-imported"><Eye className="h-3.5 w-3.5" /> Show all</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => bulkVisibility(false)} data-testid="bulk-hide-imported"><EyeOff className="h-3.5 w-3.5" /> Hide all</Button>
          </div>
        </div>
      )}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : updates.length === 0 ? (
        <EmptyState icon={FileText} title="No updates yet" description="An update is the monthly or weekly story you send investors. TenX drafts it from your metrics; you edit and approve every word." steps={["Enter this month's metrics first (or load sample data from Overview)", "Draft with AI from your numbers and notes, then edit the narrative", "Pick full email, portal link, or email-verified link and publish"]} actionLabel="Create update" onAction={() => navigate("/app/updates/new")} secondaryLabel="Add metrics first" secondaryTo="/app/metrics" testId="empty-state-updates" />
      ) : (
        <div className="space-y-2">
          {updates.map((u) => (
            <Card key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4" data-testid={`update-item-${u.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 break-words text-sm font-semibold text-foreground">{u.title}</span>
                  {u.status === "published" ? (
                    <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Published</Badge>
                  ) : u.status === "scheduled" ? (
                        <Badge className="border-amber-200 bg-amber-50 text-amber-800">Scheduled</Badge>
                  ) : u.status === "imported" ? (
                    <Badge className="border-amber-200 bg-amber-50 text-amber-700" data-testid={`imported-badge-${u.id}`}>Imported</Badge>
                  ) : (
                    <Badge className="border-slate-200 bg-slate-50 text-slate-700">Draft</Badge>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{formatPeriodLabel(u.period)} · {u.status === "published" ? `Published ${formatDate(u.published_at)}` : u.status === "imported" ? `Imported · originally ${formatDate(u.published_at)}` : `Updated ${formatDate(u.updated_at)}`}{u.reactions_total > 0 && <span className="ml-1.5 inline-flex items-center gap-1 text-primary" data-testid={`update-reactions-${u.id}`}><ThumbsUp className="h-3 w-3" />{u.reactions_total}</span>}</div>
                {u.status !== "imported" && <div className="mt-1 text-xs text-muted-foreground" data-testid={`update-delivery-mode-${u.id}`}>{u.delivery_settings?.mode === "verified_link" ? "Email-verified shared link" : u.delivery_settings?.mode === "portal_link" ? "Investor portal link" : "Full email"} · {u.delivery_settings?.track_engagement === false ? "Tracking off" : "Tracking on"}</div>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {u.status === "published" && u.delivery_settings?.mode !== "portal_link" && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => share(u)} data-testid={`share-update-${u.id}`}>
                    <Share2 className="h-3.5 w-3.5" /> {u.share_token ? "Copy link" : "Share"}
                  </Button>
                )}
                {u.status === "published" && u.share_token && (
                  <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-red-600" onClick={() => revokeShare(u)} data-testid={`revoke-share-${u.id}`}>
                    <Link2Off className="h-3.5 w-3.5" /> Revoke
                  </Button>
                )}
                {u.status === "imported" && (
                  <div className="flex items-center gap-2 border-r pr-2 mr-0.5" data-testid={`imported-visibility-${u.id}`}>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Show this imported update in investor portals">
                      <Switch checked={u.investor_visible !== false} onCheckedChange={(v) => toggleMasterVisibility(u, v)} data-testid={`visible-toggle-${u.id}`} />
                      Investors
                    </label>
                    <Button variant="outline" size="sm" className="gap-1.5" disabled={u.investor_visible === false} onClick={() => openVisDialog(u)} data-testid={`per-investor-${u.id}`}>
                      <Users className="h-3.5 w-3.5" /> Per investor
                    </Button>
                  </div>
                )}
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/app/updates/${u.id}/edit`)} data-testid={`edit-update-${u.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> {u.status === "published" ? "View" : "Edit"}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-600" data-testid={`delete-update-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this update?</AlertDialogTitle>
                      <AlertDialogDescription>This permanently removes the update and its delivery records. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(u.id)} data-testid="delete-confirm">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
